import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "../../utils/toast";
import register from "../../assets/register.png";
import PasswordService from "../../services/passwordService";
import Loading from "../Loading";
import useFormValidation from "../../hooks/useFormValidation";

function PasswordResetConfirm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  const resetData = location.state;

  // Initialize timer from localStorage or set new timer
  useEffect(() => {
    if (!resetData?.email) return;

    const timerKey = `password_reset_timer_${resetData.email}`;
    const storedStartTime = localStorage.getItem(timerKey);
    
    if (storedStartTime) {
      // Restore existing timer from localStorage
      const startTime = parseInt(storedStartTime);
      const currentTime = Date.now();
      const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);
      const remainingTime = Math.max(0, 900 - elapsedSeconds);
      
      setTimeLeft(remainingTime);
      
      // Clear expired timer from localStorage
      if (remainingTime === 0) {
        localStorage.removeItem(timerKey);
      }
    } else if (resetData.email) {
      // Set new timer only if we have valid reset data and no existing timer
      const startTime = Date.now();
      localStorage.setItem(timerKey, startTime.toString());
      setTimeLeft(900);
    }
  }, [resetData?.email]);

  // Redirect if no reset data
  useEffect(() => {
    if (!resetData?.email) {
      toast.error("Invalid reset session. Please start over.");
      navigate("/password-reset");
    }
  }, [resetData, navigate]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && resetData?.email) {
      // Clear timer from localStorage when it expires
      const timerKey = `password_reset_timer_${resetData.email}`;
      localStorage.removeItem(timerKey);
    }
  }, [timeLeft, resetData?.email]);

  // Format time display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Form validation rules
  const validationRules = {
    pin: {
      required: true,
      minLength: 6,
      maxLength: 6,
      pattern: /^\d{6}$/,
      requiredMessage: "PIN is required",
      minLengthMessage: "PIN must be 6 digits",
      maxLengthMessage: "PIN must be 6 digits",
      patternMessage: "PIN must contain only numbers",
    },
    password: {
      required: true,
      minLength: 6,
      requiredMessage: "New password is required",
      minLengthMessage: "Password must be at least 6 characters",
    },
    password_confirmation: {
      required: true,
      requiredMessage: "Password confirmation is required",
    },
  };

  const {
    formData,
    errors: formErrors,
    isSubmitting,
    handleChange: handleInputChange,
    handleSubmit,
    setIsSubmitting,
    setErrors,
  } = useFormValidation(
    {
      pin: "",
      password: "",
      password_confirmation: "",
    },
    validationRules
  );

  const validatePasswordConfirmation = useCallback(() => {
    if (formData.password && formData.password_confirmation) {
      if (formData.password !== formData.password_confirmation) {
        setErrors(prev => ({
          ...prev,
          password_confirmation: "Passwords do not match"
        }));
        return false;
      } else {
        setErrors(prev => ({
          ...prev,
          password_confirmation: ""
        }));
        return true;
      }
    }
    return true;
  }, [formData.password, formData.password_confirmation, setErrors]);

  // Handle form submission
  const onSubmit = useCallback(async (data) => {
    if (!validatePasswordConfirmation()) {
      setIsSubmitting(false);
      return;
    }

    setIsLoading(true);
    
    try {
      await PasswordService.resetPassword(data.pin, data.password, data.password_confirmation);
      
      // Clear timer from localStorage on successful reset
      if (resetData?.email) {
        const timerKey = `password_reset_timer_${resetData.email}`;
        localStorage.removeItem(timerKey);
      }
      
      toast.success("Password reset successfully!");
      navigate("/login", { 
        state: { 
          message: "Your password has been reset successfully. Please login with your new password.",
          email: resetData.email
        } 
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message, {
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  }, [navigate, resetData?.email, setIsSubmitting, validatePasswordConfirmation]);

  const formSubmitHandler = handleSubmit(onSubmit);

  // Handle resend PIN
  const handleResendPin = useCallback(async () => {
    if (!resetData?.email) return;

    setIsLoading(true);
    try {
      await PasswordService.initiatePasswordReset(resetData.email);
      
      const timerKey = `password_reset_timer_${resetData.email}`;
      const startTime = Date.now();
      localStorage.setItem(timerKey, startTime.toString());
      
      toast.success("New PIN sent to your email!");
      setTimeLeft(900);
    } catch (error) {
      toast.error(error.message || "Failed to resend PIN");
    } finally {
      setIsLoading(false);
    }
  }, [resetData?.email]);

  if (!resetData?.email) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex flex-col justify-center items-center px-4 py-8">
      <div className="w-full max-w-md sm:max-w-lg lg:max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        
        <div 
          className="relative h-48 sm:h-56 lg:h-64 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${register})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary100/80 to-primary100">
            <div className="absolute bottom-6 left-6">
              <h1 className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold">
                Verify PIN
              </h1>
              <p className="text-white/90 text-lg sm:text-xl">
                & Set New Password
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-8">
          <div className="mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
              Enter Reset PIN
            </h2>
            <p className="text-gray-600 text-sm sm:text-base mb-4">
              We sent a 6-digit PIN to <span className="font-semibold text-primary100">{resetData.maskedEmail}</span>
            </p>
            
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
              <span className="text-sm text-gray-600">PIN expires in:</span>
              <span className={`font-mono font-bold ${timeLeft < 300 ? 'text-red-600' : 'text-primary100'}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>

          <form onSubmit={formSubmitHandler} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                6-Digit PIN
              </label>
              <input
                type="text"
                name="pin"
                value={formData.pin}
                placeholder="Enter 6-digit PIN"
                onChange={handleInputChange}
                maxLength="6"
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary100 focus:border-primary100 transition-all duration-200 text-gray-800 placeholder-gray-400 text-center text-2xl font-mono tracking-widest ${
                  formErrors.pin ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                required
                disabled={isSubmitting || isLoading}
              />
              {formErrors.pin && (
                <p className="mt-1 text-sm text-red-600">{formErrors.pin}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                New Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                placeholder="Enter new password"
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary100 focus:border-primary100 transition-all duration-200 text-gray-800 placeholder-gray-400 ${
                  formErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                required
                disabled={isSubmitting || isLoading}
              />
              {formErrors.password && (
                <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                name="password_confirmation"
                value={formData.password_confirmation}
                placeholder="Confirm new password"
                onChange={handleInputChange}
                onBlur={validatePasswordConfirmation}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary100 focus:border-primary100 transition-all duration-200 text-gray-800 placeholder-gray-400 ${
                  formErrors.password_confirmation ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                required
                disabled={isSubmitting || isLoading}
              />
              {formErrors.password_confirmation && (
                <p className="mt-1 text-sm text-red-600">{formErrors.password_confirmation}</p>
              )}
            </div>

            <div className="space-y-4">
              {isLoading || isSubmitting ? (
                <Loading fullScreen={false} message="Resetting Password..." variant="medical" size="medium" />
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting || isLoading || timeLeft === 0}
                  className="w-full py-4 bg-primary100 hover:bg-primary100/90 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-xl text-white text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Reset Password
                </button>
              )}
            </div>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={handleResendPin}
              disabled={isLoading || timeLeft > 0}
              className="text-primary100 font-semibold hover:text-primary100/80 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors duration-200 text-sm"
            >
              {timeLeft > 0 ? "Resend PIN available after timer expires" : "Resend PIN"}
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-gray-600 text-sm">
              Remember your password?{' '}
              <Link 
                to="/login" 
                className="text-primary100 font-semibold hover:text-primary100/80 transition-colors duration-200"
              >
                Back to Login
              </Link>
            </p>
          </div>

          <div className="mt-6 flex justify-center">
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Secure Reset Process</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PasswordResetConfirm;